import { SubmitHandler } from 'react-hook-form';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { useContext, useEffect, useState } from 'react';
import { GetAuditionResponse } from 'hooks/u002/useGetAuditionHooks';
import { AppFooter } from 'templates/AppFooter';
import { PageContent } from 'templates/PageContent';
import { Form } from 'templates/Form';
import { Button } from 'atoms/Button';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateGroupConfirmFormSchemaType, GroupAp } from 'commons/auditionForm';
import { useGroupConfirmFormSchema } from 'hooks/u002/useCreateGroupCofirmFormSchema';
import { LabeledRadioButtonGroup } from 'molecules/LabeledRadioButtonGroup';
import { DisabledLabeledInputField } from 'molecules/DisabledLabeledInputField';
import { useGroupRegistraionHooks } from 'hooks/u002/useGroupRegistraionHooks';
import { AppContext } from 'templates/AppContextProvider';
import LoadingModal from 'organisms/LoadingModal';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'commons/paths';
import MessageModal from 'organisms/MessageModal';
import { CopyButton } from 'atoms/CopyButton';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { TransitionButton } from 'atoms/TransitionButton';

const groupApDefaultValues = {
  group_ap: GroupAp.Individual,
  member_number: 0,
};

type GroupConfirmProps = {
  audition: GetAuditionResponse & {
    audition_id: string;
    title: string;
  };
  onNext: () => void;
};

export const GroupConfirm: React.FC<GroupConfirmProps> = (props) => {
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const { currentLang } = useContext(AppContext);
  const [groupRegistraion, groupRegistraionLoading] = useGroupRegistraionHooks();
  const [groupSubmit, setGroupSubmit] = useState(false);
  const GroupConfirmFormSchema = useGroupConfirmFormSchema();
  const { userId } = useContext(AppContext);
  const [nextPath, setNextPath] = useState('');
  const [errorModal, setErrorModal] = useState(false);
  const [updateGroupSubmit, setUpdateGroupSubmit] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm<CreateGroupConfirmFormSchemaType>({
    resolver: zodResolver(GroupConfirmFormSchema),
    shouldFocusError: true,
    defaultValues: groupApDefaultValues,
  });
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);
  const groupAp = watch('group_ap');

  const onUpdateSubmit: SubmitHandler<CreateGroupConfirmFormSchemaType> = async () => {
    setUpdateGroupSubmit(true);
  };

  const onSubmit: SubmitHandler<CreateGroupConfirmFormSchemaType> = async (form) => {
    groupRegistraion({
      request: {
        audition_id: props.audition.audition_id,
        user_id: userId,
        group_ap: form.group_ap,
        member_number: form.group_ap === GroupAp.Group ? form.member_number : 1,
        lang: currentLang,
      },
      onSuccess: (response) => {
        console.log(response);
        if (form.group_ap === GroupAp.Group) {
          setGroupSubmit(true);
          setNextPath(response.group_registration_path);
        } else {
          props.onNext();
        }
      },
      onError: (error) => {
        console.log(error);
        setErrorModal(true);
      },
    });
  };

  if (groupSubmit) {
    return (
      <>
        <AppLogoHeader />
        <PageContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Form title={tAuditionForm('TU002_124')}>
              <div className={'flex flex-col justify-center items-center m-6'}>
                <div className="flex text-[--c-text] w-full">
                  <p className="text-[--c-text] break-all block mt-2">
                    {tAuditionForm('TU002_130', { audition_title: props.audition.title })}
                  </p>
                </div>

                <div className="mt-8 w-full">
                  {tAuditionForm('TU002_131')}
                  <p className="text-[--c-error] break-all block mt-2">
                    {nextPath}
                    <span className="ml-2">
                      <CopyButton copyText={nextPath} />
                    </span>
                  </p>
                  <p className="text-[--c-text] break-all block mt-2">
                    {tAuditionForm('TU002_141', {
                      year: props.audition.end_dtm.getFullYear(),
                      month: (props.audition.end_dtm.getMonth() + 1).toString().padStart(2, '0'),
                      day: props.audition.end_dtm.getDate().toString().padStart(2, '0'),
                      hour: props.audition.end_dtm.getHours().toString().padStart(2, '0'),
                      minute: props.audition.end_dtm.getMinutes().toString().padStart(2, '0'),
                    })}
                  </p>
                </div>

                <Button
                  className="w-[10rem] mt-6"
                  label={tAuditionForm('TU002_039')}
                  type="button"
                  onClick={() => navigate(PATHS.MYPAGE)}
                />
              </div>
            </Form>
          </form>
        </PageContent>
        <AppFooter />
      </>
    );
  }

  return (
    <>
      <AppLogoHeader />
      {groupRegistraionLoading && <LoadingModal />}
      {errorModal && (
        <MessageModal
          message={tAuditionForm('TU002_050')}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={() => setErrorModal(false)}
        />
      )}
      <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
        {tAuditionForm('TU002_124')}
      </div>
      <PageContent>
        <form
          onSubmit={
            props.audition.group_ap === '2' ? handleSubmit(onUpdateSubmit) : handleSubmit(onSubmit)
          }
        >
          {updateGroupSubmit && (
            <>
              <Dialog
                open={updateGroupSubmit}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  style: {
                    color: 'var(--c-text)',
                    padding: '20px',
                    borderRadius: '10px',
                    backgroundColor: 'var(--c-background2)',
                    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <DialogTitle style={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {tAuditionForm('TU002_137')}
                </DialogTitle>
                <DialogContent className="text-center">{tAuditionForm('TU002_138')}</DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                  <TransitionButton
                    label={tAuditionForm('TU002_139')}
                    onClick={() => setUpdateGroupSubmit(false)}
                    className="mr-2"
                  />
                  <Button
                    label={tAuditionForm('TU002_140')}
                    onClick={() => {
                      setUpdateGroupSubmit(false);
                      handleSubmit(onSubmit)();
                    }}
                  />
                </DialogActions>
              </Dialog>
            </>
          )}
          <Form title={tAuditionForm('TU002_124')}>
            <div className="flex flex-col gap-8 mt-4">
              <LabeledRadioButtonGroup
                title={tAuditionForm('TU002_125')}
                id="group_ap"
                values={[
                  {
                    label: tAuditionForm('TU002_126'),
                    value: GroupAp.Individual,
                    defaultChecked: GroupAp.Individual === groupApDefaultValues.group_ap,
                    ...register('group_ap'),
                    'aria-label': tAuditionForm('TU002_126'),
                  },
                  {
                    label: tAuditionForm('TU002_127'),
                    value: GroupAp.Group,
                    defaultChecked: GroupAp.Group === groupApDefaultValues.group_ap,
                    ...register('group_ap'),
                    'aria-label': tAuditionForm('TU002_127'),
                  },
                ]}
              />
              <FieldErrorMessages messages={[errors.group_ap?.message]} />
              <DisabledLabeledInputField
                title={tAuditionForm('TU002_128')}
                placeholder={''}
                id="member_number"
                {...register('member_number', { valueAsNumber: true })}
                className="w-[25rem]"
                aria-label={tAuditionForm('TU002_128')}
                autoComplete="off"
                disabled={groupAp === GroupAp.Individual}
              />
              <FieldErrorMessages messages={[errors.member_number?.message]} />
              <div className={'h-[20px]'} />
              <div className={'flex justify-center items-center'}>
                <Button className="w-[10rem]" label={tAuditionForm('TU002_129')} type="submit" />
              </div>
            </div>
          </Form>
        </form>
      </PageContent>

      <AppFooter />
    </>
  );
};
